import React, { useMemo, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import './EmployeeSurvey.css';
import {
  SurveyTrack,
  SurveyQuestion,
  TRACK_LABELS,
  HQ_DEPARTMENTS,
  LOCATION_GROUPS,
  SCALES,
  getScaleKey,
  getSectionsForTrack,
} from './survey/surveyQuestions.ts';
import SurveyPasswordGate from './survey/SurveyPasswordGate.tsx';
import { submitSurveyResponse } from '../services/surveyApiService.ts';

type Screen = 'welcome' | 'survey' | 'review' | 'thanks';

const TRACK_OPTIONS: { id: SurveyTrack; emoji: string; title: string; sub: string; tag: string }[] = [
  { id: 'retail', emoji: '🏪', title: 'Retail', sub: 'Dispensary teams', tag: '7 sections' },
  { id: 'cultivation', emoji: '🌱', title: 'Cultivation', sub: 'Operations', tag: '7 sections' },
  { id: 'hq', emoji: '🏢', title: 'Sweetspot Brands', sub: 'Corporate', tag: '9 sections' },
];

const EmployeeSurveyContent: React.FC<{ onWrongPassword: () => void }> = ({ onWrongPassword }) => {
  const [screen, setScreen] = useState<Screen>('welcome');
  const [track, setTrack] = useState<SurveyTrack>('retail');
  const [location, setLocation] = useState<string>('');
  const [department, setDepartment] = useState<string>('');
  const [answers, setAnswers] = useState<Record<string, number | string>>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState<number>(0);
  const [openComments, setOpenComments] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const sections = useMemo(() => getSectionsForTrack(track), [track]);
  const allQuestions = useMemo(() => sections.flatMap(s => s.questions), [sections]);
  const answeredCount = useMemo(
    () => allQuestions.filter(q => answers[q.id] !== undefined && answers[q.id] !== '').length,
    [allQuestions, answers]
  );
  const totalCount = allQuestions.length;
  const pct = totalCount > 0 ? Math.round((answeredCount / totalCount) * 100) : 0;

  useEffect(() => {
    if (screen === 'survey' || screen === 'review') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [screen, activeSection]);

  const startSurvey = () => {
    if (!location) return;
    setActiveSection(0);
    setScreen('survey');
  };

  const setAnswer = (qid: string, value: number | string) => {
    setAnswers(prev => {
      if (typeof value === 'string' && value.trim() === '') {
        const next = { ...prev };
        delete next[qid];
        return next;
      }
      return { ...prev, [qid]: value };
    });
  };

  const setComment = (qid: string, value: string) => {
    setComments(prev => ({ ...prev, [qid]: value }));
  };

  const toggleComment = (qid: string) => {
    setOpenComments(prev => ({ ...prev, [qid]: !prev[qid] }));
  };

  const goNext = () => {
    if (activeSection < sections.length - 1) {
      setActiveSection(activeSection + 1);
    } else {
      setScreen('review');
    }
  };

  const goPrev = () => {
    if (activeSection > 0) setActiveSection(activeSection - 1);
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const cleanComments: Record<string, string> = {};
      Object.entries(comments).forEach(([k, v]) => {
        if (v && v.trim()) cleanComments[k] = v.trim();
      });
      await submitSurveyResponse({
        track,
        location,
        department: track === 'hq' ? department : undefined,
        answers,
        comments: cleanComments,
      });
      setScreen('thanks');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong submitting your response.';
      const isAuthError = /invalid survey password/i.test(message) || /HTTP 401/i.test(message) || /no survey password/i.test(message);
      if (isAuthError) {
        setSubmitting(false);
        onWrongPassword();
        return;
      }
      setSubmitError(message);
      setSubmitting(false);
    }
  };

  const renderQuestion = (q: SurveyQuestion, idx: number) => {
    const scaleKey = getScaleKey(q);
    const scale = SCALES[scaleKey];
    const currentAnswer = answers[q.id];
    const currentComment = comments[q.id] || '';
    const hasComment = currentComment.trim().length > 0;
    const isOpen = openComments[q.id] || hasComment;

    return (
      <div className="q" key={q.id}>
        <p className="qt">
          <span className="num">{idx + 1}.</span>
          {q.text}
          {q.cv && <span className="cvt">⟡ {q.cv}</span>}
        </p>
        {q.type === 'scale' ? (
          <div className="sr-wrap">
            <div className="sr-labels">
              <span>{scale[0]}</span>
              <span>{scale[4]}</span>
            </div>
            <div className="sr">
              {[1, 2, 3, 4, 5].map(v => (
                <div
                  key={v}
                  className={`sb${currentAnswer === v ? ' sel' : ''}`}
                  onClick={() => setAnswer(q.id, v)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setAnswer(q.id, v); }}
                >
                  {v}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <textarea
            className="ti"
            rows={3}
            placeholder="Share your thoughts…"
            value={(currentAnswer as string) || ''}
            onChange={e => setAnswer(q.id, e.target.value)}
          />
        )}
        <button
          type="button"
          className={`cmt-btn${isOpen ? ' open' : ''}`}
          onClick={() => toggleComment(q.id)}
        >
          <span className="arr">▼</span> Add comment
          {hasComment && <span className="cmt-dot" />}
        </button>
        <div className={`cmt-box${isOpen ? ' open' : ''}`}>
          <textarea
            placeholder="Optional: share more context…"
            value={currentComment}
            onChange={e => setComment(q.id, e.target.value)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="employee-survey">
      <Helmet>
        <title>Sweetspot — Employee Engagement Survey</title>
        <meta name="robots" content="noindex, nofollow" />
        <link
          href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;0,9..144,700;1,9..144,400&family=Work+Sans:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div className="lbg">
        <div className="sh s1" />
        <div className="sh s2" />
        <div className="sh s3" />
      </div>

      {screen === 'welcome' && (
        <div className="scr fadein">
          <div className="ww">
            <div className="wel">
              <div className="hb">Employee Engagement Survey 2026</div>
              <h1>Your Voice <em>Grows</em><br />Our Future</h1>
              <p className="sub">
                Across our dispensaries and cultivation facility in the Northeast, every role at Sweetspot
                matters. We're building something special in cannabis — your honest feedback helps us get
                there together.
              </p>

              <div className="vs">
                <div className="vc"><span className="dot" />Speak Up</div>
                <div className="vc"><span className="dot" />Growth Is a Process</div>
                <div className="vc"><span className="dot" />Do Good</div>
                <div className="vc"><span className="dot" />Creativity from Diversity</div>
                <div className="vc"><span className="dot" />Team Mentality</div>
              </div>

              <div className="wcard">
                <div className="wlbl"><span className="wic">👤</span> Which team are you on?</div>
                <div className="tbs">
                  {TRACK_OPTIONS.map(opt => (
                    <div
                      key={opt.id}
                      className={`tb${track === opt.id ? ' sel' : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-pressed={track === opt.id}
                      onClick={() => setTrack(opt.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setTrack(opt.id);
                        }
                      }}
                    >
                      <div className="ti">{opt.emoji}</div>
                      <div className="tt">{opt.title}</div>
                      <div className="td_">{opt.sub}</div>
                      <div className="tg">{opt.tag}</div>
                    </div>
                  ))}
                </div>
              </div>

              {track === 'hq' && (
                <div className="wcard">
                  <div className="wlbl"><span className="wic">🏷️</span> What department are you in?</div>
                  <div className="sel-wrap">
                    <select value={department} onChange={e => setDepartment(e.target.value)}>
                      <option value="">Select your department…</option>
                      {HQ_DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>
              )}

              <div className="wcard">
                <div className="wlbl"><span className="wic">📍</span> Which location are you at?</div>
                <div className="sel-wrap">
                  <select value={location} onChange={e => setLocation(e.target.value)}>
                    <option value="">Select your location…</option>
                    {LOCATION_GROUPS.map(g => (
                      <optgroup key={g.state} label={g.state}>
                        {g.locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </div>
              </div>

              <div className="anon-note">
                <span className="lock">🔒</span>
                <span>
                  This survey is completely anonymous. Your individual responses will never be shared or
                  linked back to you — we only look at team-level trends to make Sweetspot better for everyone.
                </span>
              </div>

              <button
                type="button"
                className="bbeg"
                onClick={startSurvey}
                disabled={!location}
              >
                Let's Grow Together →
              </button>
            </div>
          </div>
        </div>
      )}

      {screen === 'survey' && (
        <div className="scr fadein">
          <div className="sw">
            <div className="stop">
              <span className={`trackbadge ${track}`}>{TRACK_LABELS[track]}</span>
              <span style={{ fontSize: 12, color: 'var(--tl)' }}>{location}</span>
            </div>

            <div className="shdr">
              <h2>Section {activeSection + 1} of {sections.length}</h2>
              <div className="pmeta">
                <span className="cnt">{answeredCount} of {totalCount} answered</span>
                <span className="pct">{pct}%</span>
              </div>
              <div className="ptrk">
                <div className="pfil" style={{ width: `${pct}%` }} />
              </div>
            </div>

            <div className="tabs">
              {sections.map((s, i) => {
                const done = s.questions.filter(q => answers[q.id] !== undefined && answers[q.id] !== '').length === s.questions.length;
                return (
                  <div
                    key={s.id}
                    className={`tab${i === activeSection ? ' act' : ''}`}
                    role="button"
                    tabIndex={0}
                    aria-current={i === activeSection ? 'step' : undefined}
                    onClick={() => setActiveSection(i)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setActiveSection(i);
                      }
                    }}
                  >
                    <span>{s.icon}</span>
                    <span>{s.title}</span>
                    {done && <span className="chk">✓</span>}
                  </div>
                );
              })}
            </div>

            <div className="scard">
              <div className="stitle">
                <div className="sico">{sections[activeSection].icon}</div>
                <h3>{sections[activeSection].title}</h3>
              </div>
              {sections[activeSection].questions.map((q, i) => renderQuestion(q, i))}
            </div>

            <div className="nrow">
              <button type="button" className="bprev" onClick={goPrev} disabled={activeSection === 0}>
                ← Previous
              </button>
              <button type="button" className="bnext" onClick={goNext}>
                {activeSection === sections.length - 1 ? 'Review & Submit →' : 'Next Section →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {screen === 'review' && (
        <div className="scr fadein">
          <div className="rw">
            <div className="rev">
              <h1>Ready to Submit?</h1>
              <p>You answered {answeredCount} of {totalCount} questions. Here's your snapshot:</p>
              <div className="scds">
                {sections.map(s => {
                  const sq = s.questions.filter(q => q.type === 'scale');
                  const answered = sq.filter(q => typeof answers[q.id] === 'number');
                  const avg = answered.length > 0
                    ? (answered.reduce((acc, q) => acc + (answers[q.id] as number), 0) / answered.length).toFixed(1)
                    : '—';
                  return (
                    <div className="scd" key={s.id}>
                      <div className="si">{s.icon}</div>
                      <div className="sv">{avg}</div>
                      <div className="sn">{s.title}</div>
                    </div>
                  );
                })}
              </div>
              <div className="rbtns">
                <button type="button" className="bprev" onClick={() => setScreen('survey')}>
                  ← Review Answers
                </button>
                <button type="button" className="bsub" onClick={submit} disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit Survey ✓'}
                </button>
              </div>
              <div className={`submit-status${submitError ? ' error' : ''}`}>
                {submitError || (submitting ? 'Sending your responses…' : '')}
              </div>
            </div>
          </div>
        </div>
      )}

      {screen === 'thanks' && (
        <div className="scr fadein">
          <div className="tw">
            <div className="thx">
              <h1>Thank You!</h1>
              <p>
                Your feedback has been planted. From the grow room to the sales floor to the home office —
                we're building the best team in cannabis, together.
              </p>
              <div className="vs" style={{ marginTop: 20 }}>
                <div className="vc"><span className="dot" />Speak Up ✓</div>
                <div className="vc"><span className="dot" />Growth Is a Process ✓</div>
                <div className="vc"><span className="dot" />Do Good ✓</div>
                <div className="vc"><span className="dot" />Creativity from Diversity ✓</div>
                <div className="vc"><span className="dot" />Team Mentality ✓</div>
              </div>
              <p style={{ fontSize: 12, color: 'var(--tl)' }}>You can close this tab.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EmployeeSurvey: React.FC = () => (
  <SurveyPasswordGate>
    {clearPassword => <EmployeeSurveyContent onWrongPassword={clearPassword} />}
  </SurveyPasswordGate>
);

export default EmployeeSurvey;
